/// <amd-module name="VSS/Features/Monaco/MonacoProviderProxy" />
import { IFilesHubViewService, IGitFilesService, IGitItemContent } from "VSS/Features/Monaco/Contracts";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssThemeService } from "VSS/Platform/Theming";

interface ISimplifiedMonacoModel {
    uri: monaco.Uri,
    _languageIdentifier: string,
    version: string,
    versionType: number | undefined,
    commitId?: string,
}

interface ISimplifiedCodeLensModel {
    uri: monaco.Uri
}

export interface ILanguageProvider {
    declarationProvider?: IDeclarationProvider,
    definitionProvider?: IDefinitionProvider,
    documentSemanticTokensProvider?: IDocumentSemanticTokensProvider,
    documentSymbolProvider?: IDocumentSymbolProvider,
    foldingRangeProvider?: IFoldingRangeProvider,
    hoverProvider?: IHoverProvider,
    implementationProvider?: IImplementationProvider,
    referenceProvider?: IReferenceProvider,
    typeDefinitionProvider?: ITypeDefinitionProvider,
    codeLensProvider?: ICodeLensProvider,
}

export interface IDeclarationProvider {
    provideDeclaration(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

export interface IDefinitionProvider {
    provideDefinition(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

/**
 * API for providing server or language service aware colorization to supplement or replace
 * the default rule-based syntatic colorization.
 */
export interface IDocumentSemanticTokensProvider {

    /**
     * Token name => color and font style rules for light theme.
     * 
     * Note that these rules may change the colorization of the built in syntax tokens as well
     * if the names match.
     */
    getLightThemeTokenTextStyles(): Promise<monaco.editor.ITokenThemeRule[]>,

    /**
     * Token name => color and font style rules for dark theme.
     * 
     * Note that these rules may change the colorization of the built in syntax tokens as well
     * if the names match.
     */
    getDarkThemeTokenTextStyles(): Promise<monaco.editor.ITokenThemeRule[]>,

    /**
     * Returns an object enumerating language specific token types and modifiers.
     * 
     * This legend is used as a lookup table to decode the tokens array into specific styles.
     */
    getLegend(): Promise<monaco.languages.SemanticTokensLegend>,

    /**
     * Called by the editor to get the encoded locations and styles of semantic tokens.
     * @param model The Monaco data model.
     * @param token A cancellation token for aborting the operation.
     */
    provideDocumentSemanticTokens(model: ISimplifiedMonacoModel, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.SemanticTokens>
}

export interface IDocumentSymbolProvider {
    provideDocumentSymbols(model: ISimplifiedMonacoModel, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.DocumentSymbol[]>,
}

export interface IFoldingRangeProvider {
    provideFoldingRanges(model: ISimplifiedMonacoModel, context: monaco.languages.FoldingContext, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.FoldingRange[]>,
}

export interface IHoverProvider {
    provideHover(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.Hover>,
}

export interface IImplementationProvider {
    provideImplementation(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

export interface IReferenceProvider {
    provideReferences(model: ISimplifiedMonacoModel, position: monaco.Position, context: monaco.languages.ReferenceContext, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation[]>,
}

export interface ITypeDefinitionProvider {
    provideTypeDefinition(model: ISimplifiedMonacoModel, position: monaco.Position, token: monaco.CancellationToken): monaco.languages.ProviderResult<IPreprocessedLocation | IPreprocessedLocation[]>,
}

export interface ICodeLensProvider {
    provideCodeLenses(model: ISimplifiedCodeLensModel, token: monaco.CancellationToken): monaco.languages.ProviderResult<monaco.languages.CodeLensList>,
}

export interface IPreprocessedLocation extends monaco.languages.Location {
    preUri: MonacoUri,
}

class MonacoUri {
    constructor(
        public authority: string,
        public fragment: string,
        public path: string,
        public query: string,
        public scheme: string,
        public isSameRepo?: boolean,
        public fsPath?: string,
        public version?: string,
        public versionType?: number,
        public languageId?: string) { }

    toString(): string {
        let uriString = `${this.scheme}://${this.authority}`;
        if (this.scheme == "inmemory") {
            uriString += `${this.path}`;
        }
        if (this.query != "") {
            uriString += `?${this.query}`;
            if (this.scheme != "inmemory") {
                uriString += `&path=${this.path}`;
            }
        }
        if (this.fragment != "") {
            uriString += `#${this.fragment}`;
        }
        return uriString;
    }

    static isValid(obj: { [key: string]: any }): boolean {
        if (obj && obj["isMonacoUri"]) {
            return true;
        }
        return false;
    }

    static parseAsPreUri(obj: { [key: string]: any }): MonacoUri {
        const { authority, fragment, path, query, scheme, isSameRepo, fsPath, version, versionType, languageId } = obj;
        return new MonacoUri(authority, fragment, path, query, scheme, isSameRepo, fsPath, version, versionType, languageId);
    }

    static parse(obj: MonacoUri): monaco.Uri {
        return Object.assign(new monaco.Uri(), { authority: obj.authority, scheme: obj.scheme, path: obj.path, query: obj.query, fragment: obj.fragment, toString: obj.toString });
    }
}

export class MonacoProviderProxy implements monaco.languages.CodeLensProvider,
    monaco.languages.DeclarationProvider,
    monaco.languages.DefinitionProvider,
    monaco.languages.DocumentSemanticTokensProvider,
    monaco.languages.DocumentSymbolProvider,
    monaco.languages.FoldingRangeProvider,
    monaco.languages.HoverProvider,
    monaco.languages.ImplementationProvider,
    monaco.languages.ReferenceProvider,
    monaco.languages.TypeDefinitionProvider {
    private pageContext: IVssPageContext;
    private commitId?: string;
    private languageProvider?: ILanguageProvider;
    private legend?: monaco.languages.SemanticTokensLegend;

    constructor(pageContext: IVssPageContext, contribution: ILanguageProvider, commitId?: string) {
        this.pageContext = pageContext;
        this.languageProvider = contribution;
        this.commitId = commitId;
    }

    registerProviders(languageId: string, capabilities: { [key: string]: boolean }, codeLensContext: monaco.languages.CodeLensList | undefined, disposables?: monaco.IDisposable[]) {
        if (codeLensContext) {
            this.languageProvider!.codeLensProvider = {
                provideCodeLenses: function (_model: monaco.editor.ITextModel, _token: monaco.CancellationToken) {
                    return codeLensContext;
                }
            }
        }

        for (const providerType in this.languageProvider) {
            if (capabilities[providerType]) {
                let provider;
                switch (providerType) {
                    case "declarationProvider":
                        provider = monaco.languages.registerDeclarationProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "definitionProvider":
                        provider = monaco.languages.registerDefinitionProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "documentSemanticTokensProvider":
                        this.initializeSemanticTokensProvider(languageId, disposables);
                        break;
                    case "documentSymbolProvider":
                        provider = monaco.languages.registerDocumentSymbolProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "foldingRangeProvider":
                        provider = monaco.languages.registerFoldingRangeProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "hoverProvider":
                        provider = monaco.languages.registerHoverProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "implementationProvider":
                        provider = monaco.languages.registerImplementationProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "referenceProvider":
                        provider = monaco.languages.registerReferenceProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "typeDefinitionProvider":
                        provider = monaco.languages.registerTypeDefinitionProvider(languageId, this);
                        disposables?.push(provider);
                        break;
                    case "codeLensProvider":
                        provider = monaco.languages.registerCodeLensProvider(languageId, this.languageProvider.codeLensProvider!);
                        disposables?.push(provider);
                        break;
                    default:
                        console.log(`unexpected provider type: ${providerType}`);
                        break;
                }
                console.log(`${providerType} registered`);
            }
        }
    }

    addDiscoverability(delayedHighlighting: (ranges: monaco.IRange[], theme: string) => void, locations: monaco.IRange[], theme: string) {
        if (locations.length > 0) {
            if (delayedHighlighting) {
                delayedHighlighting(locations, theme);
            }
        }
    }

    private async initializeSemanticTokensProvider(languageId: string, disposables: monaco.IDisposable[] | undefined): Promise<void> {

        // getLegend in Monaco API is not async, so do our async call first, and save in a field.
        this.legend = await this.languageProvider?.documentSemanticTokensProvider?.getLegend();

        // Set a custom theme with additional extension-provided colors for semantic tokens.
        if (!await this.trySetSemanticTokensAwareTheme()) {
            return;
        }

        // Finally, enable semantic colorization.
        const provider = monaco.languages.registerDocumentSemanticTokensProvider(languageId, this);
        disposables?.push(provider);
    }

    private async trySetSemanticTokensAwareTheme(): Promise<boolean> {
        // Figure out whether the current Azure DevOps theme is a dark or light mode theme.
        const themeService = this.pageContext.getService<IVssThemeService>('IVssThemeService');
        const isDarkMode = themeService.getCurrentTheme().isDark;

        // Request dark or light semantic tokens rules from the extension.
        const additionalRules: monaco.editor.ITokenThemeRule[] | undefined = isDarkMode ?
            await this.languageProvider?.documentSemanticTokensProvider?.getDarkThemeTokenTextStyles() :
            await this.languageProvider?.documentSemanticTokensProvider?.getLightThemeTokenTextStyles();

        if (!additionalRules) {
            return false;
        }

        const baseThemeName: monaco.editor.BuiltinTheme = isDarkMode ? 'vs-dark' : 'vs';

        // Create a custom theme for light or dark mode that adds extension-provided colors
        // for the semantic tokens.
        const theme: monaco.editor.IStandaloneThemeData = {
            base: baseThemeName,
            inherit: true,
            colors: {},
            rules: additionalRules
        }

        const themeName = 'vs-semantic-aware-' + (isDarkMode ? 'dark' : 'light');

        monaco.editor.defineTheme(themeName, theme);
        monaco.editor.setTheme(themeName);

        return true;
    }

    getLegend(): monaco.languages.SemanticTokensLegend {
        // Monaco's getLegend() method does not have an async signature so we have
        // to simulate it by doing all of the async work to fetch the legend ahead
        // of time and store it in a field.
        return this.legend as monaco.languages.SemanticTokensLegend;
    }

    async provideDocumentSemanticTokens(model: monaco.editor.ITextModel, _lastResultId: string, token: monaco.CancellationToken): Promise<monaco.languages.SemanticTokens | monaco.languages.SemanticTokensEdits> {
        const simplifiedModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.documentSemanticTokensProvider?.provideDocumentSemanticTokens(simplifiedModel, token);
        return results as monaco.languages.SemanticTokens;
    }

    releaseDocumentSemanticTokens(resultId: string): void {
    }

    async provideCodeLenses(model: monaco.editor.ITextModel, token: monaco.CancellationToken): Promise<monaco.languages.CodeLensList> {
        const simplifiedCodeLensModel: ISimplifiedCodeLensModel = { uri: model.uri };
        const results = await this.languageProvider?.codeLensProvider?.provideCodeLenses(simplifiedCodeLensModel, token);
        return results as monaco.languages.CodeLensList;
    }

    async provideDeclaration(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.declarationProvider?.provideDeclaration(simplifiedModel, position, token);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    async provideDefinition(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.definitionProvider?.provideDefinition(simplifiedModel, position, token);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    async provideDocumentSymbols(model: monaco.editor.ITextModel, token: monaco.CancellationToken): Promise<monaco.languages.DocumentSymbol[]> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.documentSymbolProvider?.provideDocumentSymbols(simplifiedModel, token);
        return results as monaco.languages.DocumentSymbol[];
    }

    async provideFoldingRanges(model: monaco.editor.ITextModel, context: monaco.languages.FoldingContext, token: monaco.CancellationToken): Promise<monaco.languages.FoldingRange[]> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.foldingRangeProvider?.provideFoldingRanges(simplifiedModel, context, token);
        return results as monaco.languages.FoldingRange[];
    }

    async provideHover(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Hover> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.hoverProvider?.provideHover(simplifiedModel, position, token);
        return results as monaco.languages.Hover;
    }

    async provideImplementation(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.implementationProvider?.provideImplementation(simplifiedModel, position, token);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    async provideReferences(model: monaco.editor.ITextModel, position: monaco.Position, context: monaco.languages.ReferenceContext, token: monaco.CancellationToken): Promise<monaco.languages.Location[]> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.referenceProvider?.provideReferences(simplifiedModel, position, context, token);
        await this.postProcessUris(results);
        return results as monaco.languages.Location[];
    }

    async provideTypeDefinition(model: monaco.editor.ITextModel, position: monaco.Position, token: monaco.CancellationToken): Promise<monaco.languages.Definition> {
        const simplifiedModel: ISimplifiedMonacoModel = await this.simplifyMonacoTextModel(model);
        const results = await this.languageProvider?.typeDefinitionProvider?.provideTypeDefinition(simplifiedModel, position, token);
        await this.postProcessUris(results);
        return results as monaco.languages.Definition;
    }

    private async getGitItem(uri: monaco.Uri, version: string, versionType: number): Promise<IGitItemContent | undefined> {
        let gitItem: IGitItemContent | undefined = undefined;
        // example version - my/CustomBranch/Name
        // example path - "/git/repoUri/GBdev/my/CustomBranch/Name/src/my/CustomFile/Path.cs"
        // pathComponents splits by /
        // ['', git, repoUri, and so on]
        const pathComponents = uri.path.split("/");
        const ref = pathComponents[3]; // GB / GC
        const isCommit = ref[1] === 'C';
        let slashesCount = 0;

        // if it is not PR view (isCommit = true) and version != null (a branch is passed in)
        // we would want to account for all the slashes in the version name
        // my/CustomBranch/Name
        // the following logic counts the number of slashes in the branch name
        if (!isCommit && version !== null) {
            slashesCount = version.split("/").length - 1;
        }

        // Once all the slashes are accounted for this takes the filepath after the branchName (GB<branchName>)
        // It ommits the first slash on the /src/my/CustomFile/Path.cs
        // filePath = src/my/CustomFile/Path.cs
        const filePath = pathComponents.slice(4 + slashesCount).join("/");
        try {
            const gitFilesService = this.pageContext?.getService<IGitFilesService>("IGitFilesService");
            if (filePath && gitFilesService) {
                gitItem = await gitFilesService!.getItemContentPromise(filePath, { version: version, versionOptions: 0, versionType: versionType });
            }
        }
        finally {
            return gitItem;
        }
    }

    // Because monaco's TextModel object is too large to send via rpc, this creates an object with only the relevant properties to send over rpc
    private async simplifyMonacoTextModel(fullTextModel: { [key: string]: any }): Promise<ISimplifiedMonacoModel> {
        let version: string = "";
        let versionType: number | undefined;
        const uri: monaco.Uri = monaco.Uri.parse(fullTextModel["uri"]);
        try {
            const filesService = this.pageContext?.getService<IFilesHubViewService>("IFilesHubViewService");
            version = filesService!.getVersion().version;
            versionType = filesService!.getVersion().versionType;

            // Since we are making a call to the git service to get the commit Id before anyway
            // if we have already retrieved the commit Id in Languages.ts then we would just be using it
            // here. If the commit Id retrieval failed then we would make a call to get the GitItem to retreive a commit Id
            if (!this.commitId) {
                const gitItem: IGitItemContent | undefined = await this.getGitItem(uri, version, versionType);
                this.commitId = gitItem?.commitId;
            }
        }
        finally {
            return {
                uri: uri,
                _languageIdentifier: fullTextModel["_languageIdentifier"],
                version: version,
                versionType: versionType,
                commitId: this.commitId,
            };
        }
    }

    private addModelIfNotExists(content: string, language: string, uri: monaco.Uri): void {
        const model = monaco.editor.getModel(uri);
        if (!model) {
            monaco.editor.createModel(content, language, uri);
        }
    }

    private async postProcessUris(results: IPreprocessedLocation[] | IPreprocessedLocation | null | undefined): Promise<void> {
        if (results instanceof Array) {
            for (const res of results) {
                this.convertSingleUri(res);
            }
            const slimResults = this.getUniqueUris(results);
            for (const r of slimResults) {
                await this.getFileContentForPeek(r);
            }
        } else if (results) {
            this.convertSingleUri(results);
            await this.getFileContentForPeek(results);
        }
    }

    private async getFileContentForPeek(result: IPreprocessedLocation): Promise<void> {
        if (result.preUri.isSameRepo) {
            const filesService = this.pageContext?.getService<IGitFilesService>("IGitFilesService");
            if (filesService) {
                const content = await filesService!.getItemContentPromise(`/${result.preUri.fsPath}`, { version: result.preUri.version!, versionOptions: 0, versionType: result.preUri.versionType! });
                this.addModelIfNotExists(content.details.content, result.preUri.languageId!, result.uri);
            }
        }
    }

    private convertSingleUri(r: IPreprocessedLocation): void {
        r.preUri = MonacoUri.parseAsPreUri(r.preUri);
        r.uri = MonacoUri.parse(r.preUri);
    }

    private getUniqueUris(results: IPreprocessedLocation[]): IPreprocessedLocation[] {
        const slimResults: IPreprocessedLocation[] = [];
        const uris: Set<string> = new Set<string>();
        for (const r of results) {
            const preUri = r.preUri.toString();
            if (!uris.has(preUri)) {
                uris.add(preUri);
                slimResults.push(r);
            }
        }
        return slimResults;
    }
}